//#region Imports

import { Component, computed, Input, OnInit, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { ButtonTypeEnum } from '../../../shared/components/button/types/button-type.enum';
import { ButtonRadiusEnum } from '../../../shared/components/button/types/button-radius.enum';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { ToastService } from '../../../shared/components/toast/service/toast.service';
import type { Certificate } from '../../../shared/models/interfaces/certificate.interface';
import type { UserCourse } from '../../../shared/models/interfaces/user-course.interface';
import { CertificateService } from '../../../shared/services/certificate/certificate.service';
import { PermissionService } from '../../../shared/services/permission/permission.service';
import { PermissionEnum, ResourceEnum } from '../../../../environments/shared-environment/permission';

//#endregion

export type ProfileCertificatesState = 'loading' | 'error' | 'ready' | 'forbidden';

export interface PendingCertificateCourse {
  courseId: string;
  title: string;
  completedAt?: Date | null;
}

@Component({
  selector: 'l-profile-certificates',
  standalone: true,
  imports: [DatePipe, ButtonComponent, IconComponent, SpinnerComponent],
  templateUrl: './profile-certificates.component.html',
})
export class ProfileCertificatesComponent implements OnInit {

  //#region Constructor

  constructor(
    private readonly certificateService: CertificateService,
    private readonly permissionService: PermissionService,
    private readonly toastService: ToastService,
  ) {}

  //#endregion

  //#region Inputs

  @Input()
  public set completedCourses(value: UserCourse[] | null | undefined) {
    this.completedCoursesSignal.set(value ?? []);
  }

  //#endregion

  //#region Public Properties

  public readonly buttonTypeEnum: typeof ButtonTypeEnum = ButtonTypeEnum;

  public readonly buttonRadiusEnum: typeof ButtonRadiusEnum = ButtonRadiusEnum;

  public readonly state = signal<ProfileCertificatesState>('loading');

  public readonly errorMessage = signal<string>('');

  public readonly certificates = signal<Certificate[]>([]);

  public readonly downloadingIds = signal<ReadonlySet<string>>(new Set());

  public readonly issuingCourseIds = signal<ReadonlySet<string>>(new Set());

  public get canIssue(): boolean {
    return this.permissionService.has(ResourceEnum.CERTIFICATES, PermissionEnum.CREATE);
  }

  public readonly pendingCourses = computed<PendingCertificateCourse[]>(() => {
    const certifiedCourseIds = new Set(this.certificates().map(certificate => certificate.courseId));

    return this.completedCoursesSignal()
      .filter(userCourse => !certifiedCourseIds.has(userCourse.courseId))
      .map(userCourse => ({
        courseId: userCourse.courseId,
        title: userCourse.course?.title ?? 'Curso concluído',
        completedAt: userCourse.completedAt ?? null,
      }));
  });

  public readonly isEmpty = computed<boolean>(() => this.certificates().length === 0 && this.pendingCourses().length === 0);

  //#endregion

  //#region Private Properties

  private readonly completedCoursesSignal = signal<UserCourse[]>([]);

  //#endregion

  //#region Lifecycle

  public async ngOnInit(): Promise<void> {
    await this.loadCertificates();
  }

  //#endregion

  //#region Public Methods

  public async loadCertificates(): Promise<void> {
    if (!this.permissionService.has(ResourceEnum.CERTIFICATES, PermissionEnum.VIEW)) {
      this.state.set('forbidden');
      return;
    }

    this.state.set('loading');
    this.errorMessage.set('');

    try {
      this.certificates.set(await this.certificateService.getMine());
      this.state.set('ready');
    } catch (error: unknown) {
      this.errorMessage.set(this.toMessage(error, 'Não foi possível carregar seus certificados.'));
      this.state.set('error');
    }
  }

  public async onDownload(certificate: Certificate): Promise<void> {
    if (this.isDownloading(certificate.id)) return;

    this.downloadingIds.update(ids => this.withItem(ids, certificate.id));

    try {
      await this.certificateService.download(certificate);
    } catch (error: unknown) {
      this.toastService.error(this.toMessage(error, 'Não foi possível baixar o certificado.'), 'Oops...');
    } finally {
      this.downloadingIds.update(ids => this.withoutItem(ids, certificate.id));
    }
  }

  public async onIssue(course: PendingCertificateCourse): Promise<void> {
    if (!this.canIssue || this.isIssuing(course.courseId)) return;

    this.issuingCourseIds.update(ids => this.withItem(ids, course.courseId));

    try {
      const certificate = await this.certificateService.issue(course.courseId);

      this.certificates.update(list => [certificate, ...list.filter(item => item.id !== certificate.id)]);
      this.toastService.success('Certificado emitido com sucesso.', 'Pronto!');
    } catch (error: unknown) {
      this.toastService.error(this.toMessage(error, 'Não foi possível emitir o certificado.'), 'Oops...');
    } finally {
      this.issuingCourseIds.update(ids => this.withoutItem(ids, course.courseId));
    }
  }

  public isDownloading(certificateId: string): boolean {
    return this.downloadingIds().has(certificateId);
  }

  public isIssuing(courseId: string): boolean {
    return this.issuingCourseIds().has(courseId);
  }

  public formatDuration(minutes: number | null | undefined): string {
    if (!minutes || minutes <= 0) return '';

    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;

    if (!hours) return `${rest}min`;

    return rest ? `${hours}h ${rest}min` : `${hours}h`;
  }

  //#endregion

  //#region Private Methods

  private withItem(ids: ReadonlySet<string>, id: string): ReadonlySet<string> {
    return new Set([...ids, id]);
  }

  private withoutItem(ids: ReadonlySet<string>, id: string): ReadonlySet<string> {
    const next = new Set(ids);
    next.delete(id);

    return next;
  }

  private toMessage(error: unknown, fallback: string): string {
    return error instanceof Error && error.message ? error.message : fallback;
  }

  //#endregion

}
